import { MouseEvent } from 'react';
import styled from '@emotion/styled';
import { Box, Button, SxProps } from '@mui/material';

import Icon from '@/components/icon/icon';

type B3PaginationProps = {
  count: number;
  onPageChange: (_: MouseEvent<HTMLButtonElement> | null, page: number) => void;
  page: number;
  rowsPerPage: number;
  sx?: SxProps;
};

const PageItem = styled('li')(() => ({
  alignItems: 'center',
  border: '0.5px solid rgba(0,0,0,0.1)',
  borderRadius: '8px',
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
}));

function B3Pagination(props: B3PaginationProps) {
  const { count = 1, onPageChange, page = 1, rowsPerPage = 8, sx = {} } = props;
  const total = Math.ceil(count / rowsPerPage);

  if (total <= 1) {
    return null;
  }

  return (
    <Box
      component="ul"
      sx={{
        display: 'flex',
        gap: '8px',
        height: '32px',
        listStyle: 'none',
        m: '32px 0 0',
        p: 0,
        ...sx,
      }}
    >
      {Array.from({ length: total }).map((_, index) => {
        const key = `page-${index}`;
        const disabled = page === index;

        return (
          <PageItem key={key}>
            <Button
              disabled={disabled}
              onClick={(event) => onPageChange(event, index)}
              title={String(index + 1)}
              sx={{
                borderRadius: '8px',
                color: '#000',
                fontFamily: '"DIN 2014","Roboto","Helvetica","Arial",sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                height: '100%',
                letterSpacing: '0.15px',
                lineHeight: '24px',
                p: '4px',
                width: '100%',
                '&:disabled': {
                  bgcolor: 'rgba(0,0,0,0.04)',
                  color: '#000',
                },
                ...(disabled && {
                  bgcolor: 'rgba(0,0,0,0.04)',
                }),
              }}
              type="button"
            >
              {index + 1}
            </Button>
          </PageItem>
        );
      })}
      {total > 1 ? (
        <>
          <PageItem>
            <Button
              disabled={page === 0}
              onClick={(event) => onPageChange(event, page - 1)}
              sx={{
                borderRadius: '8px',
                color: '#000',
                fontFamily: '"DIN 2014","Roboto","Helvetica","Arial",sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                height: '100%',
                letterSpacing: '0.15px',
                lineHeight: '24px',
                p: '4px',
                width: '100%',
                '&:disabled': {
                  bgcolor: '#fff',
                  color: '#969696',
                },
              }}
              type="button"
            >
              <Icon
                height={20}
                id="icon-arrow"
                style={{ transform: 'rotate(180deg)' }}
                width={20}
              />
            </Button>
          </PageItem>
          <PageItem>
            <Button
              disabled={page + 1 === total}
              onClick={(event) => onPageChange(event, page + 1)}
              sx={{
                borderRadius: '8px',
                color: '#000',
                fontFamily: '"DIN 2014","Roboto","Helvetica","Arial",sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                height: '100%',
                letterSpacing: '0.15px',
                lineHeight: '24px',
                p: '4px',
                width: '100%',
                '&:disabled': {
                  bgcolor: '#fff',
                  color: '#969696',
                },
              }}
              type="button"
            >
              <Icon height={20} id="icon-arrow" width={20} />
            </Button>
          </PageItem>
        </>
      ) : null}
    </Box>
  );
}

export default B3Pagination;
